import * as React from 'react'

import { describeDbError, getSupabase } from '@/lib/supabase'

/**
 * Fotos de perfil (ADR-0013).
 *
 * O bucket `avatars` é privado: rosto de colaborador não fica em URL pública.
 * Em vez de uma URL assinada por `<img>` (uma requisição por avatar, dezenas
 * por quadro), o provider assina **em lote** todos os caminhos conhecidos,
 * numa requisição só, e renova antes de expirar.
 */

const BUCKET = 'avatars'
/** Validade das URLs assinadas; o provider renova com folga antes disso. */
const SIGNED_TTL_SECONDS = 60 * 60
const RENEW_MS = 50 * 60 * 1000
/** Lado do quadrado salvo. 256px cobre o maior avatar da tela em tela 2x. */
const AVATAR_SIDE = 256
export const AVATAR_MAX_BYTES = 1024 * 1024

type UrlMap = ReadonlyMap<string, string>

const AvatarUrlContext = React.createContext<UrlMap>(new Map())

export function AvatarUrlProvider({
  paths,
  children,
}: {
  /** Caminhos de foto conhecidos (fichas carregadas). A ordem não importa. */
  paths: readonly string[]
  children: React.ReactNode
}) {
  const [urls, setUrls] = React.useState<UrlMap>(() => new Map())
  // Chave estável: o array muda de identidade a cada render do store.
  const key = React.useMemo(() => [...new Set(paths)].sort().join('|'), [paths])

  React.useEffect(() => {
    const wanted = key ? key.split('|') : []
    if (wanted.length === 0) return
    let cancelled = false

    async function sign() {
      const { data, error } = await getSupabase()
        .storage.from(BUCKET)
        .createSignedUrls(wanted, SIGNED_TTL_SECONDS)
      if (cancelled || error || !data) return
      const next = new Map<string, string>()
      for (const item of data) {
        if (item.path && item.signedUrl) next.set(item.path, item.signedUrl)
      }
      setUrls(next)
    }

    void sign()
    const timer = window.setInterval(() => void sign(), RENEW_MS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [key])

  return <AvatarUrlContext.Provider value={urls}>{children}</AvatarUrlContext.Provider>
}

/** URL assinada da foto, ou `undefined` enquanto não há (ou não existe) foto. */
export function useAvatarUrl(path: string | undefined): string | undefined {
  const urls = React.useContext(AvatarUrlContext)
  return path ? urls.get(path) : undefined
}

// -----------------------------------------------------------------------------
// Upload
// -----------------------------------------------------------------------------

/**
 * Recorta o centro num quadrado e reduz para 256px em WebP. A foto do celular
 * (4 MB, 4000px) vira ~20 KB — cabe folgado no limite de 1 MB do bucket e não
 * pesa no quadro com dezenas de avatares.
 */
export async function toSquareAvatar(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file)
  const side = Math.min(bitmap.width, bitmap.height)
  const canvas = document.createElement('canvas')
  canvas.width = AVATAR_SIDE
  canvas.height = AVATAR_SIDE
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Não foi possível preparar a imagem.')
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(
    bitmap,
    (bitmap.width - side) / 2,
    (bitmap.height - side) / 2,
    side,
    side,
    0,
    0,
    AVATAR_SIDE,
    AVATAR_SIDE,
  )
  bitmap.close()
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', 0.86),
  )
  if (!blob) throw new Error('Não foi possível converter a imagem.')
  return blob
}

export type AvatarResult = { ok: true; path: string | null } | { ok: false; reason: string }

/**
 * Sobe a foto nova, grava o caminho na ficha e apaga a antiga. Arquivo novo a
 * cada troca (versão no nome): o bucket não aceita sobrescrita, e o navegador
 * não mostra a foto velha do cache.
 */
export async function uploadAvatar(
  memberId: string,
  file: File,
  previousPath: string | undefined,
): Promise<AvatarResult> {
  if (!file.type.startsWith('image/')) {
    return { ok: false, reason: 'Escolha uma imagem (PNG, JPG ou WebP).' }
  }

  let blob: Blob
  try {
    blob = await toSquareAvatar(file)
  } catch {
    return { ok: false, reason: 'Não foi possível ler essa imagem. Tente outra.' }
  }
  if (blob.size > AVATAR_MAX_BYTES) {
    return { ok: false, reason: 'A imagem ficou maior que 1 MB mesmo reduzida. Tente outra.' }
  }

  const sb = getSupabase()
  const path = `${memberId}/${Date.now()}.webp`
  const upload = await sb.storage.from(BUCKET).upload(path, blob, {
    contentType: 'image/webp',
    upsert: false,
  })
  if (upload.error) {
    return { ok: false, reason: describeDbError(upload.error, 'Não foi possível enviar a foto.') }
  }

  const saved = await sb.rpc('set_member_avatar', { p_member_id: memberId, p_path: path })
  if (saved.error) {
    await sb.storage.from(BUCKET).remove([path])
    return { ok: false, reason: describeDbError(saved.error, 'Não foi possível salvar a foto.') }
  }

  // A troca já valeu; sobrar arquivo velho no bucket não é erro para a pessoa.
  if (previousPath) await sb.storage.from(BUCKET).remove([previousPath])
  return { ok: true, path }
}

export async function removeAvatar(
  memberId: string,
  previousPath: string | undefined,
): Promise<AvatarResult> {
  const sb = getSupabase()
  const saved = await sb.rpc('set_member_avatar', { p_member_id: memberId, p_path: null })
  if (saved.error) {
    return { ok: false, reason: describeDbError(saved.error, 'Não foi possível remover a foto.') }
  }
  if (previousPath) await sb.storage.from(BUCKET).remove([previousPath])
  return { ok: true, path: null }
}
